import React, { useEffect, useMemo, useState } from 'react';
import APIService from '../services/api';
import { useConfirm } from './ConfirmDialog';

/**
 * Rattacher un besoin EXISTANT au dossier AO (27.10 : « aujourd'hui je n'ai
 * pas la possibilité d'ajouter un besoin existant sur l'AO existant »).
 *
 * Une fenêtre : la liste des besoins de l'organisation qui ne sont pas déjà
 * dans ce dossier, une recherche, un clic. Le serveur vérifie tout
 * (POST /ao/:id/besoins { besoin_id }) ; un besoin déjà dans un AUTRE dossier
 * AO est refusé en le nommant (409 `ailleurs`) — la fenêtre demande alors
 * s'il faut l'y retirer (`deplacer: true`).
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
// La date à laquelle le besoin a été créé (30.10 : « pour savoir lequel
// ajouter ») — les plus récents en tête, puisque c'est presque toujours un
// besoin ouvert pour l'appel d'offres.
const quand = (b) => b.created_at || b.createdAt || null;
const dateCourte = (v) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d) ? d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
};
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;

export default function AoRattacherBesoin({ aoId, besoinIdsDuDossier = [], onClose, onFait, showNotification }) {
  // useConfirm rend { confirm, confirmElement } — l'appeler comme une fonction
  // faisait planter le refus 409 (« l is not a function », 01.11) et la
  // fenêtre « déplacer » ne s'ouvrait jamais.
  const { confirm, confirmElement } = useConfirm();
  const [besoins, setBesoins] = useState(null);
  const [q, setQ] = useState('');
  const [occupe, setOccupe] = useState(null);

  useEffect(() => {
    let annule = false;
    APIService.getBesoins().then(r => { if (!annule) setBesoins(r.besoins || []); }).catch(() => { if (!annule) setBesoins([]); });
    return () => { annule = true; };
  }, []);

  const dans = useMemo(() => new Set((besoinIdsDuDossier || []).map(Number)), [besoinIdsDuDossier]);
  const s = q.trim().toLowerCase();
  const liste = (besoins || [])
    .filter(b => !dans.has(Number(b.id)) && (b.type_carte == null || b.type_carte === 'besoin'))
    .filter(b => !s || `${b.titre || ''} ${b.client || ''} ${b.id}`.toLowerCase().includes(s))
    .sort((a, b) => (new Date(quand(b) || 0) - new Date(quand(a) || 0)) || (Number(b.id) - Number(a.id)))
    .slice(0, 60);

  const rattacher = async (b, deplacer = false) => {
    setOccupe(b.id);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/besoins`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` },
        body: JSON.stringify({ besoin_id: b.id, ...(deplacer ? { deplacer: true } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.code === 'ailleurs') {
        const ou = (j.ailleurs || []).map(a => `« ${a.titre} »`).join(', ');
        const ok = await confirm({ title: 'Ce besoin est dans un autre dossier AO',
          message: `« ${b.titre} » est déjà rattaché au dossier ${ou || 'AO'}. Le déplacer vers ce dossier l’en retire.`,
          confirmLabel: 'Déplacer vers ce dossier' });
        if (ok) return rattacher(b, true);
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `Le rattachement a été refusé (${r.status}).`);
      showNotification?.(j.message || 'Besoin rattaché au dossier.', 'success');
      onFait?.(j);
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setOccupe(null); }
  };

  return (
    <div className="ats-modal-overlay" onClick={() => !occupe && onClose?.()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95, padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="ao-rattacher-titre" onClick={e => e.stopPropagation()} className="erp-card"
        style={{ width: '100%', maxWidth: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: '18px' }}>
        <h3 id="ao-rattacher-titre" style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Ajouter un besoin existant</h3>
        <p style={{ margin: '6px 0 12px', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>
          Le besoin rejoint ce dossier avec ses candidats ; ses documents sont rangés dans le dossier à la prochaine analyse.
        </p>
        <label style={{ display: 'block' }}>
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Rechercher un besoin</span>
          <input className="erp-field" autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Titre, client ou numéro du besoin…" style={{ width: '100%', boxSizing: 'border-box' }} />
        </label>
        <div style={{ marginTop: '10px', overflowY: 'auto', flex: 1, minHeight: '120px' }}>
          {besoins === null && <div style={{ padding: '14px 4px', fontSize: '12.5px', color: 'var(--erp-text-tertiary)' }}><BI icon="arrow-repeat" style={{ animation: 'ao-spin .7s linear infinite' }} /> Chargement des besoins…</div>}
          {besoins !== null && !liste.length && <div style={{ padding: '14px 4px', fontSize: '12.5px', color: 'var(--erp-text-tertiary)' }}>{s ? 'Aucun besoin ne correspond.' : 'Tous les besoins sont déjà dans ce dossier.'}</div>}
          {liste.map(b => (
            <button key={b.id} type="button" disabled={occupe != null} onClick={() => rattacher(b)}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '6px', borderRadius: '9px',
                border: '1px solid var(--erp-line)', background: 'var(--erp-surface-raised)', color: 'var(--erp-ink)', font: 'inherit', cursor: occupe != null ? 'default' : 'pointer', opacity: occupe != null && occupe !== b.id ? 0.6 : 1 }}>
              <BI icon={occupe === b.id ? 'arrow-repeat' : 'briefcase'} style={{ color: 'var(--erp-text-tertiary)', ...(occupe === b.id ? { animation: 'ao-spin .7s linear infinite' } : {}) }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.titre}</span>
                <span style={{ display: 'block', fontSize: '12px', color: 'var(--erp-text-secondary)' }}>{[b.client, `#${b.id}`, dateCourte(quand(b)) && `créé le ${dateCourte(quand(b))}`].filter(Boolean).join(' · ')}</span>
              </span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--erp-accent-active)' }}>Ajouter</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button type="button" className="erp-btn erp-btn--secondary" disabled={occupe != null} onClick={onClose}>Fermer</button>
        </div>
        {confirmElement}
      </div>
    </div>
  );
}
